// Google Places API wrapper service with localStorage caching

const API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY || '';

// Cache settings
const CACHE_PREFIX = 'frontseat_outlets_';
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days (long cache for outlets)
const CACHE_VERSION = '1.0';

// Track if Google Maps script is loaded
let googleMapsLoaded = false;
let googleMapsLoading = false;
const loadCallbacks = [];

/**
 * Load Google Maps JavaScript API
 * @returns {Promise} Resolves when Google Maps is loaded
 */
const loadGoogleMaps = () => {
  return new Promise((resolve, reject) => {
    if (googleMapsLoaded && window.google?.maps?.places) {
      resolve();
      return;
    }

    if (googleMapsLoading) {
      loadCallbacks.push(resolve);
      return;
    }

    googleMapsLoading = true;
    loadCallbacks.push(resolve);

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      googleMapsLoaded = true;
      googleMapsLoading = false;
      loadCallbacks.forEach((cb) => cb());
      loadCallbacks.length = 0;
    };

    script.onerror = () => {
      googleMapsLoading = false;
      loadCallbacks.forEach((cb) => cb());
      loadCallbacks.length = 0;
      reject(new Error('Failed to load Google Maps'));
    };

    document.head.appendChild(script);
  });
};

/**
 * Check if Google Places API is configured
 * @returns {boolean} True if API key is set
 */
export const isGooglePlacesConfigured = () => {
  return !!API_KEY && API_KEY !== 'your_google_places_api_key_here';
};

/**
 * Generate cache key based on location and brands
 * @param {Object} location - { lat, lng }
 * @param {number} radius - Search radius
 * @param {Array} brands - Array of brand names
 * @returns {string} Cache key
 */
const generateCacheKey = (location, radius, brands) => {
  const brandKey = brands.map(b => b.name || b).sort().join('_');
  // Round coordinates to reduce cache misses for nearby locations
  const lat = Math.round(location.lat * 1000) / 1000;
  const lng = Math.round(location.lng * 1000) / 1000;
  return `${CACHE_PREFIX}${lat}_${lng}_${radius}m_${brandKey}`;
};

/**
 * Get cached outlets from localStorage
 * @param {string} key - Cache key
 * @returns {Array|null} Cached outlets or null
 */
const getCachedOutlets = (key) => {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const data = JSON.parse(cached);

    // Check version
    if (data.version !== CACHE_VERSION) {
      localStorage.removeItem(key);
      return null;
    }

    // Check if expired
    if (Date.now() - data.timestamp > CACHE_DURATION) {
      localStorage.removeItem(key);
      return null;
    }

    console.log(`[Cache] Using cached outlets (${data.outlets.length} items, age: ${Math.round((Date.now() - data.timestamp) / 60000)} min)`);
    return data.outlets;
  } catch (error) {
    console.warn('[Cache] Error reading cache:', error);
    return null;
  }
};

/**
 * Save outlets to localStorage
 * @param {string} key - Cache key
 * @param {Array} outlets - Outlets to cache
 */
const setCachedOutlets = (key, outlets) => {
  try {
    const data = {
      version: CACHE_VERSION,
      timestamp: Date.now(),
      outlets,
    };
    localStorage.setItem(key, JSON.stringify(data));
    console.log(`[Cache] Saved ${outlets.length} outlets to cache`);
  } catch (error) {
    console.warn('[Cache] Error saving to cache:', error);
    // If localStorage is full, clear old entries
    clearOldCache();
  }
};

/**
 * Clear old cache entries when storage is full
 */
const clearOldCache = () => {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_PREFIX));
    const now = Date.now();

    keys.forEach(key => {
      try {
        const data = JSON.parse(localStorage.getItem(key));
        // Remove entries older than 3 days
        if (now - data.timestamp > 3 * 24 * 60 * 60 * 1000) {
          localStorage.removeItem(key);
        }
      } catch {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn('[Cache] Error clearing old cache:', error);
  }
};

/**
 * Search for nearby places using Google Places API
 * @param {Object} options - Search options
 * @returns {Promise<Array>} Array of places
 */
export const searchNearbyPlaces = async (options = {}) => {
  const {
    location, // { lat, lng }
    radius = 2000,
    type = 'restaurant',
    keyword = '',
  } = options;

  if (!isGooglePlacesConfigured()) {
    console.warn('Google Places API key not configured');
    return [];
  }

  try {
    // Load Google Maps if not already loaded
    await loadGoogleMaps();

    // Create a dummy map div for PlacesService (required by Google)
    const service = new window.google.maps.places.PlacesService(
      document.createElement('div')
    );

    const request = {
      location: new window.google.maps.LatLng(location.lat, location.lng),
      radius,
      type,
      keyword,
    };

    const results = await new Promise((resolve, reject) => {
      service.nearbySearch(request, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          resolve(results);
        } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          resolve([]);
        } else {
          reject(new Error(`Places API error: ${status}`));
        }
      });
    });

    return results.map(normalizePlaceResult);
  } catch (error) {
    console.error('Error fetching nearby places:', error);
    return [];
  }
};

/**
 * Search for fast food outlets near a location with caching
 * @param {Object} location - { lat, lng }
 * @param {number} radius - Search radius in meters
 * @param {Array} brands - Array of brand keywords to search for
 * @returns {Promise<Array>} Array of fast food outlets
 */
export const searchFastFoodOutlets = async (location, radius = 2000, brands = []) => {
  if (!location || !location.lat || !location.lng) {
    console.warn('[Places] Invalid location provided');
    return [];
  }

  // Check cache first
  const cacheKey = generateCacheKey(location, radius, brands);
  const cachedOutlets = getCachedOutlets(cacheKey);

  if (cachedOutlets) {
    return cachedOutlets;
  }

  // If API not configured, return empty (no demo data)
  if (!isGooglePlacesConfigured()) {
    console.warn('[Places] Google Places API key not configured');
    return [];
  }

  console.log(`[Places] Fetching outlets from API for ${brands.length} brands...`);
  const allOutlets = [];

  // Search for each brand (with rate limiting)
  for (const brand of brands) {
    try {
      const outlets = await searchNearbyPlaces({
        location,
        radius,
        type: 'restaurant',
        keyword: brand.keyword || brand.name,
      });

      outlets.forEach((outlet) => {
        outlet.brand = brand.name;
        outlet.brandColor = brand.color;
        outlet.brandIcon = brand.logo;
      });

      allOutlets.push(...outlets);

      // Small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    } catch (error) {
      console.warn(`[Places] Error searching for ${brand.name}:`, error);
    }
  }

  // Remove duplicates based on place_id
  const uniqueOutlets = Array.from(
    new Map(allOutlets.map((o) => [o.placeId, o])).values()
  );

  // Cache the results
  if (uniqueOutlets.length > 0) {
    setCachedOutlets(cacheKey, uniqueOutlets);
  }

  console.log(`[Places] Found ${uniqueOutlets.length} unique outlets`);
  return uniqueOutlets;
};

/**
 * Normalize a Google Maps JavaScript API result to our format
 * @param {Object} place - Google Place object from JS API
 * @returns {Object} Normalized place object
 */
const normalizePlaceResult = (place) => ({
  placeId: place.place_id,
  name: place.name,
  latitude: place.geometry?.location?.lat(),
  longitude: place.geometry?.location?.lng(),
  vicinity: place.vicinity,
  rating: place.rating || null,
  totalRatings: place.user_ratings_total || 0,
  priceLevel: place.price_level || null,
  isOpen: place.opening_hours?.isOpen?.() ?? null,
  types: place.types || [],
  photos: place.photos?.map((p) => p.getUrl({ maxWidth: 400 })) || [],
  icon: place.icon,
});

/**
 * Clear all places cache
 */
export const clearCache = () => {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_PREFIX));
    keys.forEach(key => localStorage.removeItem(key));
    console.log(`[Cache] Cleared ${keys.length} cached entries`);
  } catch (error) {
    console.warn('[Cache] Error clearing cache:', error);
  }
};

/**
 * Get cache statistics
 * @returns {Object} Cache stats
 */
export const getCacheStats = () => {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_PREFIX));
    let totalSize = 0;
    let validEntries = 0;

    keys.forEach(key => {
      const item = localStorage.getItem(key);
      if (item) {
        totalSize += item.length;
        try {
          const data = JSON.parse(item);
          if (Date.now() - data.timestamp <= CACHE_DURATION) {
            validEntries++;
          }
        } catch {}
      }
    });

    return {
      entries: keys.length,
      validEntries,
      sizeKB: Math.round(totalSize / 1024),
    };
  } catch {
    return { entries: 0, validEntries: 0, sizeKB: 0 };
  }
};

/**
 * Preload outlets for multiple locations (useful for batch operations)
 * @param {Array} locations - Array of { lat, lng } locations
 * @param {number} radius - Search radius
 * @param {Array} brands - Brands to search for
 */
export const preloadOutlets = async (locations, radius = 2000, brands = []) => {
  console.log(`[Places] Preloading outlets for ${locations.length} locations...`);

  for (const location of locations) {
    const cacheKey = generateCacheKey(location, radius, brands);

    // Skip if already cached
    if (getCachedOutlets(cacheKey)) {
      continue;
    }

    await searchFastFoodOutlets(location, radius, brands);

    // Delay between requests
    await new Promise(resolve => setTimeout(resolve, 200));
  }

  console.log('[Places] Preloading complete');
};
